/* eslint-disable @typescript-eslint/no-explicit-any */

import { AnyObject } from 'types';
import { ensureArray } from './array';

export function pick<Obj extends AnyObject, Key extends keyof Obj>(
  object: Obj,
  propsToKeep: Key[],
): Pick<Obj, Key> {
  const picked = Object.fromEntries(propsToKeep.map((prop) => [prop, object[prop]]));

  return picked as Pick<Obj, Key>;
}

export function omit<Obj extends AnyObject, Key extends keyof Obj>(
  object: Obj,
  propsToExclude: Key[],
): Omit<Obj, Key> {
  const curated = Object.fromEntries(
    Object.entries(object).filter(([prop]) => !propsToExclude.includes(prop as Key)),
  );

  return curated as Omit<Obj, Key>;
}

export function arePropertiesTruthy<Obj extends AnyObject>(object: Obj): boolean {
  return Object.values(object).every(Boolean);
}

export function omitUndefined<Obj extends AnyObject>(object: Obj): Obj {
  const curated = Object.fromEntries(
    Object.entries(object).filter(([_, value]) => value !== undefined),
  );

  return curated as Obj;
}

export function omitWithValues<Obj extends AnyObject>(object: Obj, values: any[] = []): Obj {
  const curated = Object.fromEntries(
    Object.entries(object).filter(([_, value]) => !values.includes(value)),
  );

  return curated as Obj;
}

export function getNumericObjectTotal(obj: Record<string, number>): number {
  const total = Object.values(obj).reduce((acc, next) => acc + next, 0);

  return total;
}

export function getDeepPropParts(property: string): string[] {
  return property.split('.').filter(Boolean);
}

// to do: recursive type
export function extractDeepProp<Result = any>(
  entity: AnyObject | AnyObject[],
  prop: string,
): NonNullable<Result>[] {
  const [firstLevel, ...rest] = getDeepPropParts(prop);

  const targets = ensureArray(entity);

  const accessed = targets.filter(Boolean).map((target) => target[firstLevel]);

  if (!rest.length) return accessed;

  return extractDeepProp(accessed, rest.join('.'));
}

export function typedEntries<Obj extends AnyObject>(obj: Obj) {
  return Object.entries(obj) as Array<[keyof Obj, Obj[keyof Obj]]>;
}

export function typedKeys<Obj extends AnyObject>(obj: Obj) {
  return Object.keys(obj) as Array<keyof Obj>;
}

/**
 * Swap the reference key of your config objects within itself.
 *
 * This is a rather complex looking fn for an actually simple call.
 *
 * Suppose you have:
 *
 * ```ts
 * const config = {
 *   done: { code: '0', label: 'Done' },
 *   active: { code: '1', label: 'Active' },
 *   canceled: { code: '2', label: 'Canceled' },
 * }
 * ```
 *
 * Sometimes yoy indeed need to directly access the X status config,
 * but generally you want to do:
 *
 * ```ts
 * const currentConfig = configByStatus[entity.status]
 * ```
 *
 * This helper function generates your `configByStatus` from the start `config`!
 */
export function reKeyBy<Obj extends Record<string, AnyObject>>(
  obj: Obj,
  refKey: keyof Obj[keyof Obj],
): Record<string, Obj[keyof Obj]> {
  const switchedEntries = Object.values(obj).map((config) => [
    config[refKey as keyof typeof config],
    config,
  ]);

  return Object.fromEntries(switchedEntries);
}
